/**
 * Upgrading a formula stored under an older regime to the current one.
 *
 * Three things have changed over the life of this format:
 *
 *   - v1 → v2: a bare `[field]` used to mean the CURRENT ROW; v2 spells that `[@field]` and lets
 *     `[field]` mean the whole column.
 *   - v2 → v3: the grammar dropped `!` between containers in favour of dot notation.
 *   - v2 → v3: storage stopped being TEXT and became a structured AST keyed by ids (`stored.ts`).
 *
 * The last of those is what makes this simple to get right: a v1/v2 value is a *string* and a v3
 * value is an *object*, so they cannot be confused, and the upgrade is trivially idempotent — an
 * object is already current and is handed straight back.
 *
 * This runs on READ (`normaliseGridFormulas`, `decodeOverrideValue`) rather than as a batch
 * migration, and the new shape persists on the next save. Deliberately not written in plpgsql: the
 * conversion needs a real parser, which the database has no business hosting.
 */

import { parseCanonical, rewriteLegacySeparators, type AstNode } from './language'
import { rawFormula, storedFromCanonical, toStored, type StoredFormula } from './stored'

/** The version a stored string was written under. Missing means the oldest. */
export type LegacySyntaxVersion = 1 | 2

/**
 * v1's rule, applied to the tree: a reference with no explicit row meant the current row.
 *
 * Done on the AST rather than on the text because that is where the rule actually lives — a
 * scanner would have to re-implement quoting and escaping just to find the brackets. A
 * cross-sheet reference is left alone: it could not name a row under v1 either.
 */
function currentRowByDefault(node: AstNode): AstNode {
  switch (node.kind) {
    case 'ref':
      return node.row === 'column' && node.sheetId === undefined && node.documentId === undefined
        ? { ...node, row: 'current' }
        : node
    case 'call':
      return { ...node, args: node.args.map(currentRowByDefault) }
    case 'binary':
      return { ...node, left: currentRowByDefault(node.left), right: currentRowByDefault(node.right) }
    case 'unary':
    case 'percent':
    case 'group':
      return { ...node, arg: currentRowByDefault(node.arg) }
    // A range names its own rows, and the leaves carry nothing to reinterpret.
    case 'range':
    case 'number':
    case 'text':
    case 'raw':
    case 'name':
      return node
  }
}

/**
 * Old canonical TEXT → the stored form. Text the parser cannot read becomes a `raw` formula rather
 * than being dropped: a formula the user has to repair is worth more than nothing.
 */
export function upgradeV2Text(text: string, from: LegacySyntaxVersion = 2): StoredFormula {
  const rewritten = rewriteLegacySeparators(text)
  if (from === 2) return storedFromCanonical(rewritten)
  const parsed = parseCanonical(rewritten)
  return parsed.ok ? toStored(currentRowByDefault(parsed.ast)) : rawFormula(rewritten)
}

/**
 * Any persisted formula value → the current stored form.
 *
 * `undefined` means "there was no formula here", which is different from a formula that failed to
 * upgrade; callers use it to skip the entry entirely.
 */
export function upgradeStoredFormula(
  value: unknown,
  from: LegacySyntaxVersion = 2,
): StoredFormula | undefined {
  // Already current — including a `raw` one, which must NOT be re-parsed: its text is text the
  // parser already refused, and re-running it would only produce the same node again.
  if (typeof value === 'object' && value !== null && !Array.isArray(value)
    && (value as Record<string, unknown>).v === 3) {
    return value as StoredFormula
  }
  if (typeof value === 'string') {
    return value.trim() === '' ? undefined : upgradeV2Text(value, from)
  }
  return undefined
}

/** The version marker off a stored config, defaulting to the oldest when it is missing. */
export function legacyVersionOf(marker: unknown): LegacySyntaxVersion {
  return marker === 2 || marker === 3 ? 2 : 1
}
